"use client";

import { useTStream } from "#/hooks/use-t-stream";
import {
  BRAND_ASSET_ARCHIVE_NAME,
  BRAND_ASSET_DOWNLOAD_PATH,
  isStreamBrandedSite,
} from "#/lib/brand-assets";
import { toast } from "#/lib/toast";
import { cn } from "#/lib/utils";
import { ThemeContext } from "#/providers/theme-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#/ui/dropdown-menu";
import { StreamLogo } from "#/ui/logo";
import Loader from "#/ui/standard-loader";
import { downloadBlob } from "#/utils/download.utils";
import { ChevronDownIcon, DownloadIcon } from "lucide-react";
import { PointerEvent, useContext, useEffect, useRef, useState } from "react";

/** Grace period so the pointer can cross the gap between logo and menu. */
const HOVER_CLOSE_DELAY_MS = 200;

/**
 * Enough to clear the nav bar, whose height is set by the taller region select
 * beside the logo. Anything less and the menu's top rows render *behind* the
 * nav, which paints over the portalled content.
 */
const NAV_CLEARANCE_PX = 32;

/**
 * The logo doubles as the brand menu: hovering it reveals a chevron and opens a
 * dropdown, the way Resend's wordmark does.
 *
 * Stream-branded sites only. On a white-label shell this renders the partner's
 * logo exactly as before — plain, inert, no chevron — because the archive holds
 * Stream's marks and nobody browsing a partner's login page should be offered
 * them. See `isStreamBrandedSite`.
 *
 * Only mouse hover opens it — touch and pen fall through to Radix's own tap
 * handling, because on a phone "hover" is just the start of a tap and a menu
 * that pops open under your finger is a misfire, not a feature.
 *
 * The download fetches the archive rather than pointing a plain `<a download>`
 * at the route: an anchor would navigate the user to the raw JSON body if the
 * route 502s, whereas fetching lets us keep them on the page and surface a
 * toast. The request is same-origin, so no CORS concerns here — the
 * cross-origin problem lives in the route handler, which is why it exists.
 */
export function BrandAssetMenu({ className }: { className?: string }) {
  const { t } = useTStream("common");
  const theme = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Hover-opened menus must not steal focus; keyboard-opened ones must. */
  const openedByPointer = useRef(false);
  /** Where focus was sitting when a hover opened the menu. */
  const focusBeforeHover = useRef<HTMLElement | null>(null);

  const cancelScheduledClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  useEffect(() => cancelScheduledClose, []);

  // After the hooks, never before them.
  if (!isStreamBrandedSite(theme)) {
    return (
      <div className={cn("flex items-center", className)}>
        <StreamLogo />
      </div>
    );
  }

  const handlePointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    cancelScheduledClose();
    openedByPointer.current = true;
    focusBeforeHover.current = document.activeElement as HTMLElement | null;
    setIsOpen(true);
  };

  const handlePointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    cancelScheduledClose();
    closeTimer.current = setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const response = await fetch(BRAND_ASSET_DOWNLOAD_PATH);
      if (!response.ok) {
        throw new Error(`Brand asset export failed: ${response.status}`);
      }
      downloadBlob(await response.blob(), BRAND_ASSET_ARCHIVE_NAME);
      setIsOpen(false);
    } catch (error) {
      console.error("[brand-assets] export failed", error);
      toast.error(t("brandAssetsExportFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        cancelScheduledClose();
        if (!open) openedByPointer.current = false;
        setIsOpen(open);
      }}
      open={isOpen}
    >
      <DropdownMenuTrigger
        aria-label={t("brandAssets")}
        className={cn(
          "group flex items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-secondary",
          className,
        )}
        onKeyDown={() => {
          openedByPointer.current = false;
        }}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        <StreamLogo />
        <ChevronDownIcon
          aria-hidden="true"
          className="h-4 w-4 text-secondary-foreground opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60 group-data-[state=open]:opacity-60"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64"
        // A hover-opened menu grabs focus (Radix doesn't expose
        // `onOpenAutoFocus` here), which would yank the caret out of whatever
        // the visitor was typing. Hand focus straight back and tell Radix that
        // focus leaving isn't a dismissal.
        onFocus={(event) => {
          // Only the container's own auto-focus — arrow-key item focus is real
          // navigation and must stand.
          if (
            !openedByPointer.current ||
            event.target !== event.currentTarget
          ) {
            return;
          }
          const previous = focusBeforeHover.current;
          if (previous?.isConnected) previous.focus();
          else event.currentTarget.blur();
        }}
        onFocusOutside={(event) => {
          if (openedByPointer.current) event.preventDefault();
        }}
        onPointerEnter={cancelScheduledClose}
        onPointerLeave={handlePointerLeave}
        sideOffset={NAV_CLEARANCE_PX}
      >
        <DropdownMenuItem
          className="gap-3 py-2"
          disabled={isExporting}
          // Keep the menu up while the archive is being built so the row can
          // show its own progress; `handleExport` closes it on success and
          // leaves it open on failure, so a retry is one click away.
          onSelect={(event) => {
            event.preventDefault();
            void handleExport();
          }}
        >
          {isExporting ? (
            <Loader
              className="flex h-4 w-4 shrink-0 items-center justify-center"
              size={14}
            />
          ) : (
            <DownloadIcon
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-muted"
            />
          )}
          <span className="flex flex-col gap-0.5">
            {t("brandAssetsDownload")}
            <span className="text-muted text-xs">
              {t("brandAssetsExportHint")}
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
